import React, { useEffect, useRef } from 'react';
import {
  Calendar,
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Table2,
} from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import {
  AUDIT_TRAIL_ACK_STATUS_OPTIONS,
  AUDIT_TRAIL_CRITICALITY_OPTIONS,
} from '../../constants/acknowledgementAuditTrail.constants';
import type { AuditTrailExportFormat } from '../../types/acknowledgementAuditTrail.types';
import type { CriticalResultsFilterOption } from '../criticalResultsDashboard/CriticalResultsFilterBar';

interface AcknowledgementAuditTrailFilterBarProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  facilityOptions: CriticalResultsFilterOption[];
  facilityId: number | null;
  onFacilityChange: (value: number) => void;
  showFacilityFilter: boolean;
  criticalityBand: string;
  onCriticalityBandChange: (value: string) => void;
  acknowledgementStatus: string;
  onAcknowledgementStatusChange: (value: string) => void;
  clinician: string;
  onClinicianChange: (value: string) => void;
  clinicianOptions: Array<{ value: string; label: string }>;
  search: string;
  onSearchChange: (value: string) => void;
  onClear: () => void;
  loading?: boolean;
  showExport?: boolean;
  exporting?: boolean;
  exportMenuOpen?: boolean;
  onExportMenuToggle?: () => void;
  onExport?: (format: AuditTrailExportFormat) => void;
}

function formatFilterDate(isoDate: string): string {
  if (!isoDate) {
    return '';
  }
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

function FilterDate({
  label,
  value,
  onChange,
  min,
  max,
}: Readonly<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
}>) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <div className="crd-filter-field">
      <span className="crd-filter-label">{label}</span>
      <label
        className="crd-filter-control crd-filter-control--date"
        htmlFor={`aat-date-${label}`}
      >
        <span className="crd-filter-value">{formatFilterDate(value)}</span>
        <Calendar
          className="crd-filter-icon"
          size={16}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
        <input
          ref={inputRef}
          id={`aat-date-${label}`}
          type="date"
          className="crd-filter-date-input"
          value={value}
          min={min}
          max={max}
          onChange={event => onChange(event.target.value)}
        />
      </label>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: Readonly<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}>) {
  const selectId = `aat-select-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className="crd-filter-field">
      <label
        className="crd-filter-label"
        htmlFor={selectId}
      >
        {label}
      </label>
      <div className="crd-filter-control crd-filter-control--select">
        <select
          id={selectId}
          className="crd-filter-select"
          value={value}
          onChange={event => onChange(event.target.value)}
        >
          {options.map(option => (
            <option
              key={option.value || '__all__'}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="crd-filter-icon crd-filter-icon--select"
          size={16}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
      </div>
    </div>
  );
}

export default function AcknowledgementAuditTrailFilterBar({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  facilityOptions,
  facilityId,
  onFacilityChange,
  showFacilityFilter,
  criticalityBand,
  onCriticalityBandChange,
  acknowledgementStatus,
  onAcknowledgementStatusChange,
  clinician,
  onClinicianChange,
  clinicianOptions,
  search,
  onSearchChange,
  onClear,
  loading = false,
  showExport = false,
  exporting = false,
  exportMenuOpen = false,
  onExportMenuToggle,
  onExport,
}: Readonly<AcknowledgementAuditTrailFilterBarProps>) {
  const exportWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onExportMenuToggle) {
      return undefined;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (exportWrapRef.current && !exportWrapRef.current.contains(event.target as Node)) {
        if (exportMenuOpen) {
          onExportMenuToggle();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportMenuOpen, onExportMenuToggle]);

  const exportOptions: Array<{
    format: AuditTrailExportFormat;
    label: string;
    Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  }> = [
    { format: 'pdf', label: 'Export as PDF', Icon: FileText },
    { format: 'csv', label: 'Export as CSV', Icon: Table2 },
    { format: 'xlsx', label: 'Export as Excel', Icon: FileSpreadsheet },
  ];

  return (
    <section className="crd-filter-section">
      <div className="crd-filters aat-filters">
        <FilterDate
          label="From"
          value={startDate}
          onChange={onStartDateChange}
          max={endDate}
        />
        <FilterDate
          label="To"
          value={endDate}
          onChange={onEndDateChange}
          min={startDate}
        />
        {showFacilityFilter ? (
          <FilterSelect
            label="Facility"
            value={facilityId != null ? String(facilityId) : ''}
            onChange={value => onFacilityChange(Number(value))}
            options={facilityOptions}
          />
        ) : null}
        <FilterSelect
          label="Criticality"
          value={criticalityBand}
          onChange={onCriticalityBandChange}
          options={AUDIT_TRAIL_CRITICALITY_OPTIONS}
        />
        <FilterSelect
          label="Ack status"
          value={acknowledgementStatus}
          onChange={onAcknowledgementStatusChange}
          options={AUDIT_TRAIL_ACK_STATUS_OPTIONS}
        />
        <FilterSelect
          label="Referring clinician"
          value={clinician}
          onChange={onClinicianChange}
          options={clinicianOptions}
        />
        <div className="crd-filter-field aat-search-field">
          <label
            className="crd-filter-label"
            htmlFor="aat-search"
          >
            Search
          </label>
          <div className="crd-filter-control">
            <input
              id="aat-search"
              type="text"
              className="crd-filter-text-input"
              value={search}
              placeholder="Patient / study / accession"
              onChange={event => onSearchChange(event.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="crd-filter-actions">
        <button
          type="button"
          className="crd-btn crd-btn--ghost"
          onClick={onClear}
          disabled={loading}
        >
          Clear
        </button>
        {showExport && onExport && onExportMenuToggle ? (
          <div
            className="crd-export-wrap"
            ref={exportWrapRef}
          >
            <button
              type="button"
              className="crd-btn crd-btn--secondary"
              onClick={onExportMenuToggle}
              disabled={exporting || loading}
              aria-busy={exporting}
              aria-expanded={exportMenuOpen}
              aria-haspopup="menu"
            >
              {exporting ? (
                <Loader2
                  className="crd-export-spinner"
                  size={16}
                  strokeWidth={ICON_STROKE_WIDTH}
                />
              ) : (
                <Download
                  size={16}
                  strokeWidth={ICON_STROKE_WIDTH}
                />
              )}
              {exporting ? 'Exporting…' : 'Export'}
              {exporting ? null : (
                <ChevronDown
                  size={16}
                  strokeWidth={ICON_STROKE_WIDTH}
                />
              )}
            </button>
            {exportMenuOpen && !exporting ? (
              <div
                className="crd-export-menu"
                role="menu"
              >
                {exportOptions.map(({ format, label, Icon }) => (
                  <button
                    key={format}
                    type="button"
                    role="menuitem"
                    className="crd-export-menu-item"
                    onClick={() => onExport(format)}
                  >
                    <Icon
                      size={16}
                      strokeWidth={ICON_STROKE_WIDTH}
                      aria-hidden
                    />
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
